// Components/PrintClassModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Printer, FilePdf, FileExcel } from 'react-bootstrap-icons';
import { studentAPI } from '../services/api';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import '../CSS/PrintClassModal.css';

const PrintClassModal = ({ show, onHide }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [classData, setClassData] = useState(null);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const printRef = useRef();
  const abortControllerRef = useRef(null);

  const classOptions = ['6', '7', '8', '9', '10', '11', '12'];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedClass('');
      setClassData(null);
      setError('');
      setRetryCount(0);
    }
    
    // Cleanup function to abort any pending requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [show]);

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setClassData(null);
    setError('');
    setRetryCount(0);
  };

  const fetchClassStudents = async () => {
    if (!selectedClass) {
      setError('Please select a class');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError('');
    
    try {
      console.log('Fetching students for class:', selectedClass);
      
      const response = await studentAPI.getStudentsByClass(selectedClass);
      
      if (response.data && response.data.success) {
        if (response.data.data.length === 0) {
          setError('No students found for this class');
          setClassData(null);
        } else {
          setClassData(response.data);
        }
      } else {
        setError('No students found for this class');
        setClassData(null);
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      
      console.error('Error fetching students:', err);
      
      // Auto-retry logic for timeouts
      if (err.code === 'ECONNABORTED' && retryCount < 2) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchClassStudents(), 3000);
        setError(`Connection timeout. Retrying... (${retryCount + 1}/3)`);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch students. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Grade_${selectedClass}_Students_${new Date().toLocaleDateString()}`,
    onAfterPrint: () => console.log('Print completed'),
  });

  const generatePDF = () => {
    if (!classData?.data) return;

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm'
    });

    // Title
    doc.setFontSize(18);
    doc.setTextColor(41, 128, 185);
    doc.text(`Grade ${selectedClass} Students List`, 14, 15);
    
    // Date and Statistics
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
    doc.text(`Total Students: ${classData.statistics.total}`, 14, 27);
    doc.text(`Male: ${classData.statistics.maleCount} | Female: ${classData.statistics.femaleCount}`, 14, 32);

    // Table headers
    const headers = [
      ['No.', 'Student Name', 'Gender', 'Date of Birth', 'Phone', 'Father\'s Name', 'Mother\'s Name', 'Village', 'District']
    ];

    const tableData = classData.data.map((student, index) => [
      index + 1,
      student.studentName || 'N/A',
      student.gender || 'N/A',
      student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A',
      student.phoneNumber || 'N/A',
      student.fatherName || 'N/A',
      student.motherName || 'N/A',
      student.village || 'N/A',
      student.district || 'N/A'
    ]);

    doc.autoTable({
      head: headers,
      body: tableData,
      startY: 40,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    doc.save(`Grade_${selectedClass}_Students_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const generateExcel = () => {
    if (!classData?.data) return;

    const workbook = XLSX.utils.book_new();
    
    const formattedData = classData.data.map((student, index) => ({
      'No.': index + 1,
      'Student Name': student.studentName || 'N/A',
      'Gender': student.gender || 'N/A',
      'Date of Birth': student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A',
      'Phone': student.phoneNumber || 'N/A',
      "Father's Name": student.fatherName || 'N/A',
      "Mother's Name": student.motherName || 'N/A',
      'Village': student.village || 'N/A',
      'District': student.district || 'N/A',
      'Province': student.province || 'N/A',
      'Class': student.class || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 }, { wch: 25 }, { wch: 8 }, { wch: 12 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 6 }
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, `Grade_${selectedClass}`);
    XLSX.writeFile(workbook, `Grade_${selectedClass}_Students_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const PrintableContent = () => (
    <div ref={printRef} className="printable-content">
      <style>{`
        @media print {
          body { font-family: Arial, sans-serif; }
          .print-header { text-align: center; margin-bottom: 20px; }
          .print-stats { margin: 15px 0; text-align: center; }
          .print-table { width: 100%; border-collapse: collapse; }
          .print-table th { background-color: #2c3e50; color: white; padding: 10px; font-size: 12px; }
          .print-table td { border: 1px solid #ddd; padding: 8px; font-size: 11px; }
          .print-table tr:nth-child(even) { background-color: #f9f9f9; }
          .print-footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
        }
      `}</style>
      
      <div className="print-header">
        <h1>Grade {selectedClass} Students List</h1>
        <h3>{new Date().toLocaleDateString()}</h3>
      </div>

      <div className="print-stats">
        <p>
          Total Students: {classData?.statistics.total} | 
          Male: {classData?.statistics.maleCount} | 
          Female: {classData?.statistics.femaleCount}
        </p>
      </div>

      <table className="print-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Student Name</th>
            <th>Gender</th>
            <th>Date of Birth</th>
            <th>Phone</th>
            <th>Father's Name</th>
            <th>Mother's Name</th>
            <th>Village</th>
            <th>District</th>
          </tr>
        </thead>
        <tbody>
          {classData?.data.map((student, index) => (
            <tr key={student.id}>
              <td>{index + 1}</td>
              <td>{student.studentName}</td>
              <td>{student.gender}</td>
              <td>{new Date(student.dateOfBirth).toLocaleDateString()}</td>
              <td>{student.phoneNumber}</td>
              <td>{student.fatherName}</td>
              <td>{student.motherName}</td>
              <td>{student.village}</td>
              <td>{student.district}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="print-footer">
        <p>Generated by School Management System</p>
      </div>
    </div>
  );

  const handleRetry = () => {
    setRetryCount(0);
    fetchClassStudents();
  };

 return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="pcm-modal-dialog">
      <Modal.Header closeButton className="pcm-modal-header">
        <Modal.Title className="pcm-modal-title">
          <Printer className="pcm-title-icon" />
          Print Class Student List
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pcm-modal-body">

        {/* ── Class selector ── */}
        <div className="pcm-field-group">
          <label className="pcm-field-label">Select Class / Grade</label>
          <select
            value={selectedClass}
            onChange={handleClassChange}
            className="pcm-select"
          >
            <option value="">Choose a class…</option>
            {classOptions.map(cls => (
              <option key={cls} value={cls}>Grade {cls}</option>
            ))}
          </select>
        </div>

        {/* ── Action buttons ── */}
        <div className="pcm-action-row">
          <button
            className="pcm-btn pcm-btn-load"
            onClick={fetchClassStudents}
            disabled={!selectedClass || loading}
          >
            {loading ? (
              <>
                <span className="pcm-spinner" aria-hidden="true" />
                Loading…
              </>
            ) : (
              'Load Students'
            )}
          </button>

          {classData && classData.data.length > 0 && !loading && (
            <>
              <button className="pcm-btn pcm-btn-print" onClick={handlePrint}>
                <Printer className="pcm-btn-icon" /> Print
              </button>
              <button className="pcm-btn pcm-btn-pdf" onClick={generatePDF}>
                <FilePdf className="pcm-btn-icon" /> PDF
              </button>
              <button className="pcm-btn pcm-btn-excel" onClick={generateExcel}>
                <FileExcel className="pcm-btn-icon" /> Excel
              </button>
            </>
          )}

          {error && error.includes('Retrying') && (
            <button className="pcm-btn pcm-btn-retry" onClick={handleRetry}>
              Retry Now
            </button>
          )}
        </div>

        {/* ── Error / retry alert ── */}
        {error && (
          <div className={`pcm-alert ${error.includes('Retrying') ? 'pcm-alert-warn' : 'pcm-alert-danger'}`}>
            <span className="pcm-alert-text">{error}</span>
            <button className="pcm-alert-close" onClick={() => setError('')} aria-label="Dismiss">×</button>
          </div>
        )}

        {/* ── Loading indicator ── */}
        {loading && (
          <div className="pcm-loading-state">
            <span className="pcm-spinner pcm-spinner-lg" aria-hidden="true" />
            <p className="pcm-loading-text">
              Loading students… This may take a moment on slow connections.
            </p>
          </div>
        )}

        {/* ── Preview table ── */}
        {classData && classData.data.length > 0 && !loading && (
          <div className="pcm-preview-section">
            <h5 className="pcm-preview-title">
              Preview — Grade {selectedClass} Students
            </h5>
            <p className="pcm-preview-subtitle">
              Showing first 10 of {classData.data.length} students
            </p>

            <div className="pcm-table-wrapper">
              <table className="pcm-table">
                <thead className="pcm-table-header">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Gender</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody className="pcm-table-body">
                  {classData.data.slice(0, 10).map((student, index) => (
                    <tr key={student.id}>
                      <td>{index + 1}</td>
                      <td className="pcm-cell-name">{student.studentName}</td>
                      <td>
                        <span className={`pcm-gender-badge pcm-gender-${student.gender?.toLowerCase()}`}>
                          {student.gender}
                        </span>
                      </td>
                      <td>{student.phoneNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {classData.data.length > 10 && (
                <p className="pcm-table-overflow-note">
                  … and {classData.data.length - 10} more students
                </p>
              )}
            </div>

            {/* Stats strip */}
            <div className="pcm-stats-strip">
              <span className="pcm-stat-item">
                Total <strong>{classData.statistics.total}</strong>
              </span>
              <span className="pcm-stat-divider" />
              <span className="pcm-stat-item">
                Male <strong>{classData.statistics.maleCount}</strong>
              </span>
              <span className="pcm-stat-divider" />
              <span className="pcm-stat-item">
                Female <strong>{classData.statistics.femaleCount}</strong>
              </span>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="pcm-modal-footer">
        <button className="pcm-btn pcm-btn-close-modal" onClick={onHide}>Close</button>
      </Modal.Footer>

      {/* Hidden printable content — unchanged */}
      <div style={{ display: 'none' }}>
        <PrintableContent />
      </div>
    </Modal>
  );
};

export default PrintClassModal;